import React, { useState, useEffect, useRef } from "react";
import { createRoot, Root } from "react-dom/client";

interface FeedbackConfig {
  id: number;
  name: string;
  title: string;
  show_form: boolean;
  show_list: boolean;
}

interface Review {
  id: number;
  author_name: string;
  text: string;
  rating: number;
  created_at: string;
}

interface FeedbackProps {
  feedbackName: string;
  slug: string;
  triggerButton?: HTMLElement;
  buttonText?: string;
  buttonClass?: string;
  inline?: boolean; // Если true, форма отображается inline без popup
}

type SubmitStatus = "success" | "error" | null;

/**
 * Компонент отзывов (Feedback)
 */
function Feedback({ feedbackName, slug, triggerButton, buttonText, buttonClass, inline = false }: FeedbackProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(inline);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
  const [feedbackConfig, setFeedbackConfig] = useState<FeedbackConfig | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [reviewsOffset, setReviewsOffset] = useState<number>(0);
  const [loadingReviews, setLoadingReviews] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const formRef = useRef<HTMLFormElement | null>(null);

  const openPopup = (): void => {
    setIsOpen(true);
    if (!inline) {
      document.body.style.overflow = "hidden";
    }

    // Загружаем конфигурацию, если ещё не загружена
    if (!feedbackConfig && !isLoading) {
      loadConfig();
    }
  };

  // Загружаем конфигурацию при монтировании в inline режиме
  useEffect(() => {
    if (inline && !feedbackConfig && !isLoading) {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inline]);

  const closePopup = (): void => {
    if (!inline) {
      setIsOpen(false);
      document.body.style.overflow = "";
    }
    setSubmitStatus(null);
    setSelectedRating(0);
    setHoverRating(0);
    if (formRef.current) {
      formRef.current.reset();
    }
  };

  const loadConfig = async (): Promise<void> => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`/api.php?action=get_feedback&name=${encodeURIComponent(feedbackName)}`);
      const result = await response.json();

      if (result.success) {
        setFeedbackConfig(result.data);
        // Загружаем отзывы, если нужно показывать список
        if (result.data.show_list) {
          loadReviews(0);
        }
      } else {
        setError(result.message || "Ошибка загрузки конфигурации отзывов");
      }
    } catch (err) {
      setError("Ошибка подключения к серверу");
      console.error("Feedback config loading error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadReviews = async (offset: number): Promise<void> => {
    if (loadingReviews) return;

    setLoadingReviews(true);
    try {
      const response = await fetch(`/api.php?action=get_reviews&feedback_name=${encodeURIComponent(feedbackName)}&slug=${encodeURIComponent(slug)}&limit=10&offset=${offset}`);
      const result = await response.json();

      if (result.success) {
        if (offset === 0) {
          setReviews(result.data);
        } else {
          setReviews((prev) => [...prev, ...result.data]);
        }
        setHasMore(result.has_more);
        setReviewsOffset(offset + result.data.length);
      }
    } catch (err) {
      console.error("Reviews loading error:", err);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formRef.current) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitError("");

    const formData = new FormData(formRef.current);
    formData.append("feedback_name", feedbackName);
    formData.append("slug", slug);

    try {
      const response = await fetch("/feedback.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        setSelectedRating(0);
        setHoverRating(0);
        formRef.current.reset();

        // Обновляем CAPTCHA
        const captchaImage = formRef.current.querySelector<HTMLImageElement>("img[data-captcha-image]");
        if (captchaImage) {
          captchaImage.removeAttribute("src");
          captchaImage.removeAttribute("data-loaded");
        }

        // Обновляем список отзывов, если он показывается
        if (feedbackConfig?.show_list) {
          loadReviews(0);
        }

        // Закрываем popup через 3 секунды (только если не inline)
        if (!inline) {
          setTimeout(() => {
            closePopup();
          }, 3000);
        }
      } else {
        setSubmitStatus("error");
        setSubmitError(result.message || "Произошла ошибка. Пожалуйста, попробуйте позже.");

        // Обновляем CAPTCHA при ошибке
        const captchaImage = formRef.current.querySelector<HTMLImageElement>("img[data-captcha-image]");
        if (captchaImage) {
          captchaImage.src = `/captcha.php?${Math.random()}`;
        }

        const captchaInput = formRef.current.querySelector<HTMLInputElement>('input[name="captcha"]');
        if (captchaInput) {
          captchaInput.value = "";
        }
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitStatus("error");
      setSubmitError("Ошибка подключения к серверу. Пожалуйста, проверьте подключение к интернету и попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Управление показом CAPTCHA
  useEffect(() => {
    if (!feedbackConfig || !formRef.current || !isOpen) return;

    const captchaContainer = formRef.current.querySelector<HTMLElement>(".captcha-container");
    if (captchaContainer) {
      const requiredFields = formRef.current.querySelectorAll<HTMLInputElement>('input[required]:not([name="captcha"])');

      const checkFields = (): void => {
        let allFilled = true;
        requiredFields.forEach((field) => {
          const value = field.value.trim();
          if (!value) {
            allFilled = false;
          }
        });

        if (allFilled) {
          captchaContainer.classList.remove("hidden");
          const captchaInput = formRef.current?.querySelector<HTMLInputElement>('input[name="captcha"]');
          if (captchaInput) {
            captchaInput.required = true;
          }
          // Загружаем CAPTCHA только когда контейнер становится видимым
          const captchaImage = formRef.current?.querySelector<HTMLImageElement>("img[data-captcha-image]");
          if (captchaImage && !captchaImage.dataset.loaded) {
            captchaImage.src = `/captcha.php?${Math.random()}`;
            captchaImage.dataset.loaded = "true";
          }
        } else {
          captchaContainer.classList.add("hidden");
          const captchaInput = formRef.current?.querySelector<HTMLInputElement>('input[name="captcha"]');
          if (captchaInput) {
            captchaInput.required = false;
            captchaInput.value = "";
          }
          // Сбрасываем флаг загрузки при скрытии контейнера
          const captchaImage = formRef.current?.querySelector<HTMLImageElement>("img[data-captcha-image]");
          if (captchaImage) {
            captchaImage.removeAttribute("src");
            captchaImage.removeAttribute("data-loaded");
          }
        }
      };

      requiredFields.forEach((field) => {
        field.addEventListener("input", checkFields);
        field.addEventListener("change", checkFields);
      });
      checkFields();
    }

    // Обработчик обновления CAPTCHA
    const captchaImage = formRef.current.querySelector<HTMLImageElement>("img[data-captcha-image]");
    if (captchaImage) {
      captchaImage.addEventListener("click", () => {
        captchaImage.src = `/captcha.php?${Math.random()}`;
      });
    }
  }, [feedbackConfig, isOpen]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && isOpen && !inline) {
        closePopup();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, inline]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      closePopup();
    }
  };

  const renderStars = (rating: number): JSX.Element => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg key={star} className={`w-4 h-4 ${star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  const renderRatingSelector = (): JSX.Element => {
    const displayRating = hoverRating || selectedRating;
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1" onMouseLeave={() => setHoverRating(0)}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className="focus:outline-none transition-colors"
              onMouseEnter={() => setHoverRating(star)}
              onClick={() => {
                setSelectedRating(star);
                // Обновляем скрытое поле
                const hiddenInput = formRef.current?.querySelector<HTMLInputElement>('input[name="rating"]');
                if (hiddenInput) {
                  hiddenInput.value = star.toString();
                }
              }}
              aria-label={`Оценка ${star}`}
            >
              <svg className={`w-8 h-8 transition-colors cursor-pointer ${star <= displayRating ? "text-yellow-400 fill-current hover:text-yellow-500" : "text-gray-300 hover:text-gray-400"}`} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          ))}
        </div>
        {selectedRating > 0 && (
          <span className="text-sm text-gray-600">
            {selectedRating === 5 && "Отлично"}
            {selectedRating === 4 && "Хорошо"}
            {selectedRating === 3 && "Удовлетворительно"}
            {selectedRating === 2 && "Плохо"}
            {selectedRating === 1 && "Очень плохо"}
          </span>
        )}
        <input type="hidden" name="rating" value={selectedRating} required />
      </div>
    );
  };

  // Рендерим кнопку, если popup закрыт и не inline режим
  if (!isOpen && !inline) {
    const displayText = buttonText || triggerButton?.textContent || "Оставить отзыв";
    const displayClass = buttonClass || triggerButton?.className || "btn-primary";

    return (
      <button onClick={openPopup} className={displayClass} data-trigger-feedback={feedbackName}>
        {displayText}
      </button>
    );
  }

  // Inline режим - отображаем форму и список напрямую
  if (inline) {
    return (
      <div>
        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {feedbackConfig && (
          <>
            {/* Список отзывов */}
            {feedbackConfig.show_list && (
              <div className="mb-8">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{feedbackConfig.title}</h3>
                  {reviews.length > 0 &&
                    (() => {
                      const averageRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
                      const roundedRating = Math.round(averageRating * 10) / 10; // Округляем до 1 знака после запятой
                      return (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">{renderStars(Math.round(averageRating))}</div>
                          <span className="text-sm font-semibold text-gray-700">{roundedRating.toFixed(1)}</span>
                          <span className="text-xs text-gray-500">
                            ({reviews.length} {reviews.length === 1 ? "отзыв" : reviews.length < 5 ? "отзыва" : "отзывов"})
                          </span>
                        </div>
                      );
                    })()}
                </div>
                {reviews.length === 0 && !loadingReviews ? (
                  <p className="text-gray-500">Пока нет отзывов. Будьте первым!</p>
                ) : (
                  <>
                    <div className="space-y-4">
                      {reviews.map((review) => (
                        <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-semibold text-gray-900">{review.author_name}</div>
                              <div className="text-sm text-gray-500">{new Date(review.created_at).toLocaleDateString("ru-RU")}</div>
                            </div>
                            {renderStars(review.rating)}
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">{review.text}</p>
                        </div>
                      ))}
                    </div>
                    {hasMore && (
                      <button onClick={() => loadReviews(reviewsOffset)} disabled={loadingReviews} className="mt-4 w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {loadingReviews ? "Загрузка..." : "Показать больше"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Форма отправки отзыва */}
            {feedbackConfig.show_form && (
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Оставить отзыв</h3>
                {submitStatus !== "success" && (
                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ваше имя <span className="text-red-500">*</span>
                        </label>
                        <input type="text" name="author_name" required maxLength={24} placeholder="Введите ваше имя" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                      </div>

                      <div className="">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Оценка <span className="text-red-500">*</span>
                        </label>
                        {renderRatingSelector()}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Текст отзыва <span className="text-red-500">*</span>
                      </label>
                      <textarea name="text" required maxLength={4096} rows={4} placeholder="Напишите ваш отзыв..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                      <p className="text-xs text-gray-500 mt-1">Максимум 4КБ</p>
                    </div>

                    {/* CAPTCHA контейнер */}
                    <div className="captcha-container hidden">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Код с картинки <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input type="text" name="captcha" required placeholder="Введите код" maxLength={10} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase" style={{ textTransform: "uppercase" }} />
                        <div className="flex-shrink-0">
                          <img data-captcha-image alt="CAPTCHA" className="h-12 w-auto border border-gray-300 rounded cursor-pointer" title="Нажмите для обновления" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Нажмите на картинку, чтобы обновить код</p>
                    </div>

                    {submitError && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-red-800 text-sm font-medium">{submitError}</p>
                        </div>
                      </div>
                    )}

                    <button type="submit" disabled={isSubmitting} className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                      {isSubmitting ? "Отправка..." : "Отправить отзыв"}
                    </button>
                  </form>
                )}

                {submitStatus === "success" && (
                  <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <p className="text-green-800 text-base font-medium">Спасибо! Ваш отзыв отправлен на модерацию и будет опубликован после проверки.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Popup режим
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleBackdropClick}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">{feedbackConfig?.title || "Загрузка..."}</h2>
          <button onClick={closePopup} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {isLoading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="text-gray-600 text-sm mt-2">Загрузка...</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {feedbackConfig && (
            <>
              {/* Список отзывов */}
              {feedbackConfig.show_list && (
                <div className="mb-8">
                  {reviews.length > 0 && (
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{feedbackConfig.title}</h3>
                      {(() => {
                        const averageRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
                        const roundedRating = Math.round(averageRating * 10) / 10;
                        return (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">{renderStars(Math.round(averageRating))}</div>
                            <span className="text-sm font-semibold text-gray-700">{roundedRating.toFixed(1)}</span>
                            <span className="text-xs text-gray-500">
                              ({reviews.length} {reviews.length === 1 ? "отзыв" : reviews.length < 5 ? "отзыва" : "отзывов"})
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {reviews.length === 0 && !loadingReviews ? (
                    <p className="text-gray-500">Пока нет отзывов. Будьте первым!</p>
                  ) : (
                    <>
                      <div className="space-y-4">
                        {reviews.map((review) => (
                          <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <div className="font-semibold text-gray-900">{review.author_name}</div>
                                <div className="text-sm text-gray-500">{new Date(review.created_at).toLocaleDateString("ru-RU")}</div>
                              </div>
                              {renderStars(review.rating)}
                            </div>
                            <p className="text-gray-700 whitespace-pre-wrap">{review.text}</p>
                          </div>
                        ))}
                      </div>
                      {hasMore && (
                        <button onClick={() => loadReviews(reviewsOffset)} disabled={loadingReviews} className="mt-4 w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                          {loadingReviews ? "Загрузка..." : "Показать больше"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Форма отправки отзыва */}
              {feedbackConfig.show_form && submitStatus !== "success" && (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ваше имя <span className="text-red-500">*</span>
                    </label>
                    <input type="text" name="author_name" required maxLength={24} placeholder="Введите ваше имя" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Оценка <span className="text-red-500">*</span>
                    </label>
                    {renderRatingSelector()}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Текст отзыва <span className="text-red-500">*</span>
                    </label>
                    <textarea name="text" required maxLength={4096} rows={4} placeholder="Напишите ваш отзыв..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                    <p className="text-xs text-gray-500 mt-1">Максимум 4КБ</p>
                  </div>

                  {/* CAPTCHA контейнер */}
                  <div className="captcha-container hidden">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Код с картинки <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <input type="text" name="captcha" required placeholder="Введите код" maxLength={10} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase" style={{ textTransform: "uppercase" }} />
                      <div className="flex-shrink-0">
                        <img data-captcha-image alt="CAPTCHA" className="h-12 w-auto border border-gray-300 rounded cursor-pointer" title="Нажмите для обновления" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Нажмите на картинку, чтобы обновить код</p>
                  </div>

                  {submitError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-red-800 text-sm font-medium">{submitError}</p>
                      </div>
                    </div>
                  )}

                  <button type="submit" disabled={isSubmitting} className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? "Отправка..." : "Отправить отзыв"}
                  </button>
                </form>
              )}

              {submitStatus === "success" && (
                <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-green-800 text-base font-medium">Спасибо! Ваш отзыв отправлен на модерацию и будет опубликован после проверки.</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Хранилище для уже созданных roots
const rootsMap = new WeakMap<HTMLElement, Root>();

/**
 * Инициализация компонентов отзывов
 */
export function initFeedbacks(): void {
  const containers = document.querySelectorAll<HTMLElement>(".feedback-container");

  containers.forEach((container) => {
    if (container.dataset.reactInitialized === "true") {
      return;
    }

    const feedbackName = container.dataset.feedbackName;
    const slug = container.dataset.feedbackSlug;

    if (!feedbackName || !slug) {
      console.error("Feedback container must have data-feedback-name and data-feedback-slug attributes");
      return;
    }

    // Проверяем, нужен ли inline режим
    const isInline = container.dataset.inline === "true" || container.hasAttribute("data-inline");

    // Получаем данные кнопки из атрибутов контейнера (если не inline)
    const buttonText = container.dataset.buttonText || "Оставить отзыв";
    const buttonClass = container.dataset.buttonClass || "btn-primary";
    const buttonId = container.dataset.buttonId;

    // Ищем существующую кнопку (только если не inline)
    let existingButton: HTMLElement | null = null;
    if (!isInline) {
      existingButton = buttonId ? document.getElementById(buttonId) : container.querySelector<HTMLElement>("button");
    }

    // Создаем виртуальную кнопку для передачи данных (если кнопка не найдена и не inline)
    const virtualButton = existingButton || (isInline ? undefined : document.createElement("button"));
    if (!existingButton && !isInline && virtualButton) {
      virtualButton.className = buttonClass;
      virtualButton.textContent = buttonText;
    }

    let root = rootsMap.get(container);
    if (!root) {
      root = createRoot(container);
      rootsMap.set(container, root);
    }

    root.render(<Feedback feedbackName={feedbackName} slug={slug} triggerButton={virtualButton} buttonText={buttonText} buttonClass={buttonClass} inline={isInline} />);
    container.dataset.reactInitialized = "true";
  });
}
